/**
 * Congratulations popup with music-themed fireworks animation
 * Shows a celebratory message when user unlocks a better discount
 */

import {
  createMusicFireworks,
  createConfetti,
  createSparkles,
  createFloatingNotes,
} from '../decorations/celebration-animations';

/**
 * Congratulations Manager
 */
class CongratulationsManager {
  private isShowing = false;

  /**
   * Show congratulations popup with fireworks animation
   * @param discountPercent - The discount percentage to display
   * @param onClose - Optional callback when popup closes
   */
  show(discountPercent: number, onClose?: () => void): void {
    if (this.isShowing) return;
    this.isShowing = true;

    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'congratulations-backdrop';

    // Create overlay for fireworks
    const overlay = document.createElement('div');
    overlay.className = 'congratulations-overlay';

    // Create popup
    const popup = document.createElement('div');
    popup.className = 'congratulations-popup';
    popup.innerHTML = `
            <div class="congrats-content">
                <h2>🎉 Congratulations! 🎉</h2>
                <p class="congrats-message">You've unlocked an even better discount!</p>
                <div class="discount-highlight">
                    <span class="discount-value">${discountPercent}% OFF</span>
                </div>
                <p class="congrats-enjoy">Enjoy your exclusive musical journey!</p>
                <button id="congrats-close-btn" class="congrats-button">Continue to Your Offer</button>
            </div>
        `;

    // Add elements to DOM
    document.body.appendChild(backdrop);
    document.body.appendChild(overlay);
    document.body.appendChild(popup);

    // Create fireworks animations
    createMusicFireworks(overlay);
    createConfetti(overlay);
    createSparkles(overlay);
    createFloatingNotes(overlay);

    // Close handler
    const closePopup = () => {
      this.isShowing = false;

      // Add fade-out animation
      backdrop.classList.add('fade-out');
      overlay.classList.add('fade-out');
      popup.classList.add('fade-out');

      setTimeout(() => {
        backdrop.remove();
        overlay.remove();
        popup.remove();
        if (onClose) {
          onClose();
        }
      }, 300);
    };

    // Set up close button
    const closeBtn = popup.querySelector('#congrats-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', closePopup);
    }

    // Allow clicking backdrop to close
    backdrop.addEventListener('click', closePopup);

    // Add entrance animation
    setTimeout(() => {
      backdrop.classList.add('show');
      popup.classList.add('show');
    }, 10);
  }
}

// Create singleton instance
export const CongratulationsPopup = new CongratulationsManager();
